import { NgClass } from "@angular/common";
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { MatButtonToggle, MatButtonToggleGroup } from "@angular/material/button-toggle";
import { ClassDirective } from "@ngbracket/ngx-layout/extended";
import { FlexDirective, LayoutAlignDirective, LayoutDirective } from "@ngbracket/ngx-layout/flex";
import { LgdDatePickerComponent, LgdFormControlFactory } from "@tylertech/lgd-reactive-forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { DateHelpers } from "../../dateHelpers";
export interface OnDatesChangeArgs
{
    startDate: Date;
    endDate: Date;
}
@Component({
    selector: "common-chart-date-options",
    templateUrl: "./commonChartDateOptions.tmpl.html",
    imports: [
        ClassDirective,
        FlexDirective,
        LayoutAlignDirective,
        LayoutDirective,
        LgdDatePickerComponent,
        MatButtonToggle,
        MatButtonToggleGroup,
        NgClass,
        ReactiveFormsModule
    ]
})
export class CommonChartDateOptionsComponent implements OnInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();

    @Input() startDate: Date;
    @Input() endDate: Date;
    @Input() selectedDateOption: string;
    @Input() dateOptions: ReadonlyArray<string>;

    @Output() readonly onDateChange = new EventEmitter<OnDatesChangeArgs>();

    @Output() readonly onDateOptionChange = new EventEmitter<string>();

    readonly chartOptionsForm: FormGroup;

    protected get beginDateControl(): AbstractControl<Date> { return this.chartOptionsForm.get("beginDate"); }
    protected get endDateControl(): AbstractControl<Date> { return this.chartOptionsForm.get("endDate"); }
    protected get dateOptionControl(): AbstractControl<string> { return this.chartOptionsForm.get("dateOption"); }

    constructor()
    {
        this.chartOptionsForm = new FormGroup({
            beginDate: LgdFormControlFactory.date(),
            endDate: LgdFormControlFactory.date(),
            dateOption: new FormControl<string>(undefined)
        });
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    /**
     * ! Currently This Component Expects Only The Top Chart In a Common Chart Component Has Date Options.
     */
    ngOnInit(): void
    {
        const self = this;

        this.beginDateControl.setValue(this.startDate);
        subscribeToValueChanges(this.beginDateControl, (newValue: Date) =>
        {
            if (!DateHelpers.areEqual(this.startDate, newValue))
            {
                const args: OnDatesChangeArgs = {
                    startDate: this.beginDateControl.value,
                    endDate: this.endDateControl.value,
                };
                this.onDateChange.emit(args);
            }
        });

        this.endDateControl.setValue(this.endDate);
        subscribeToValueChanges(this.endDateControl, (newValue: Date) =>
        {
            if (!DateHelpers.areEqual(this.endDate, newValue))
            {
                const args: OnDatesChangeArgs = {
                    startDate: this.beginDateControl.value,
                    endDate: this.endDateControl.value,
                };
                this.onDateChange.emit(args);
            }
        });

        subscribeToValueChanges(this.dateOptionControl, (newValue: string) =>
        {
            this.selectedDateOption = newValue;
            this.onDateOptionChange.emit(newValue);
        });

        function subscribeToValueChanges<T = unknown>(control: AbstractControl<T>, subscriptionFn: (newValue: T) => void): void
        {
            control.valueChanges.pipe(takeUntil(self.destructor$))
                .subscribe(x =>
                {
                    subscriptionFn(x);
                });
        }
    }

    getDateOptionClass(dateOption: string): Record<string, boolean>
    {
        // ! TODO - matToggleButton doesn't handle this for us?
        /* eslint-disable @typescript-eslint/naming-convention */
        return {
            "toggle-button-checked": dateOption === this.selectedDateOption,
            // "mat-button-toggle-checked": dateOption === this.selectedDateOption,
        };
        /* eslint-enable @typescript-eslint/naming-convention */
    }

    getDateOptionAriaLabel(dateOption: string): string
    {
        return `Submission-${dateOption}`;
    }
}
